import {
  collection, doc, getDocs, increment, limit, query, serverTimestamp, setDoc, updateDoc, orderBy,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { sendPlatformNotification, getPublicPlatformRuntime, friendlyError } from './platformIntegrationService';

const STAGE_ORDER = ['account', 'verification', 'profile', 'interests', 'company', 'package', 'payment', 'extra_seat_payment', 'company_approval', 'completed'];

export const asDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value?.toDate === 'function') { try { return value.toDate(); } catch (_) { return null; } }
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const registrationStageOf = (user = {}) => {
  const status = String(user.registrationStatus || '').toLowerCase();
  const raw = String(user.registrationStage || user.stage || status || '').toLowerCase();
  if (status === 'expired' || raw === 'expired') return 'expired';
  if (status === 'declined' || raw === 'declined') return 'declined';
  if (user.registrationIncomplete === false && (user.profileCompleted === true || raw === 'completed' || status === 'active')) return 'completed';
  if (raw.includes('extra') && raw.includes('seat')) return 'extra_seat_payment';
  if (raw.includes('company') && raw.includes('approval')) return 'company_approval';
  if (raw.includes('verification')) return 'verification';
  if (user.emailVerified === false && user.whatsappVerified !== true && user.verificationOverride !== true && user.isVerified !== true) return 'verification';
  if (raw.includes('payment') || raw.includes('subscription')) return 'payment';
  if (raw.includes('package') || raw.includes('plan')) return 'package';
  if (raw.includes('company')) return 'company';
  if (raw.includes('interest')) return 'interests';
  if (raw.includes('profile') || user.profileCompleted === false) return 'profile';
  return raw || 'account';
};

export const isIncompleteRegistration = (user = {}) => {
  if (user.registrationIncomplete === true) return true;
  const status = String(user.registrationStatus || '').toLowerCase();
  const stage = String(user.registrationStage || user.stage || '').toLowerCase();
  if (['pending_extra_seat_payment','pending_extra_seat_payment_review','pending_company_approval','payment_pending','verification_pending','profile_incomplete'].includes(status)) return true;
  if (status.includes('pending') && (user.registrationStartedAt || user.registrationStage)) return true;
  if (stage && !['completed','active','done'].includes(stage)) return true;
  if (user.profileCompleted === false && (user.registrationStartedAt || user.registrationStage || user.registrationStatus)) return true;
  return false;
};

export const registrationProgress = (user = {}) => {
  const stage = registrationStageOf(user);
  const idx = Math.max(0, STAGE_ORDER.indexOf(stage));
  const safeIdx = idx < 0 ? 0 : idx;
  return Math.round((safeIdx / (STAGE_ORDER.length - 1)) * 100);
};

export const humanRegistrationStage = (user = {}) => {
  const stage = registrationStageOf(user);
  const labels = {
    account: 'Account created', verification: 'Awaiting verification', profile: 'Profile incomplete', interests: 'Interests incomplete',
    company: 'Company setup pending', package: 'Package selection pending', payment: 'Payment pending', extra_seat_payment: 'Extra-seat payment',
    company_approval: 'Company approval pending', completed: 'Completed', expired: 'Expired', declined: 'Declined',
  };
  return labels[stage] || stage.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};


export async function resolveRegistrationResumeUrl(user = {}) {
  if (user.registrationResumeUrl || user.resumeRegistrationUrl) return user.registrationResumeUrl || user.resumeRegistrationUrl;
  const runtime = await getPublicPlatformRuntime().catch(() => ({}));
  const base = runtime.companyPortalUrl || runtime.registration?.companyPortalUrl || runtime.email?.verificationBaseUrl || process.env.REACT_APP_COMPANY_PORTAL_URL || '';
  if (!base) return '';
  return `${String(base).replace(/\/$/, '')}/login?continue=register`;
}

export async function sendRegistrationReminder(user, channels = ['in_app', 'email', 'whatsapp']) {
  if (!user?.id && !user?.uid) throw new Error('Registration account was not found.');
  const uid = user.id || user.uid;
  const runtime = await getPublicPlatformRuntime().catch(() => ({}));
  const companyPortalUrl = user.registrationResumeUrl || runtime.companyPortalUrl || runtime.registration?.companyPortalUrl || runtime.email?.verificationBaseUrl || '';
  const registrationLink = user.registrationResumeUrl || (companyPortalUrl ? `${String(companyPortalUrl).replace(/\/$/, '')}/login?continue=register` : '');
  const title = 'Complete your TELCOFAIR registration';
  const message = registrationLink
    ? `Your TELCOFAIR registration is still incomplete. Continue from your saved stage: ${registrationLink}`
    : 'Your TELCOFAIR registration is still incomplete. Sign in to continue from your saved stage.';
  const selected = new Set(channels);
  const payload = {
    recipients: selected.has('in_app') ? [{ uid }] : [],
    title,
    message,
    type: 'registration_reminder',
  };
  if (selected.has('email') && user.email) payload.email = { to: user.email, operation: 'registration_completion', variables: { firstName: user.firstName || '', fullName: user.displayName || '', registrationLink, registrationStage: registrationStageOf(user) } };
  if (selected.has('whatsapp') && (user.phone || user.phoneNumber)) payload.whatsapp = { phone: user.phone || user.phoneNumber, operation: 'registration_completion', message, variables: { registrationLink } };
  if (selected.has('sms') && (user.phone || user.phoneNumber)) payload.sms = { phone: user.phone || user.phoneNumber, message, operation: 'registration_completion' };
  const result = await sendPlatformNotification(payload);
  if (!Array.isArray(result?.results) || result.results.length === 0) {
    throw new Error('None of the selected notification channels are enabled or configured.');
  }
  const historyRef = doc(collection(db, 'users', uid, 'registrationReminderHistory'));
  await setDoc(historyRef, {
    userId: uid,
    channels: Array.from(selected),
    title,
    message,
    result: result?.results || [],
    triggeredBy: 'super_admin',
    createdAt: serverTimestamp(),
  });
  await setDoc(doc(db, 'users', uid), {
    registrationReminderCount: increment(1),
    registrationReminderLastAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: true });
  return result;
}

export async function getRegistrationReminderHistory(uid) {
  try {
    const snap = await getDocs(query(collection(db, 'users', uid, 'registrationReminderHistory'), orderBy('createdAt', 'desc'), limit(50)));
    return snap.docs.map((row) => ({ id: row.id, ...row.data() }));
  } catch (error) {
    console.warn('Reminder history unavailable:', error?.message || error);
    return [];
  }
}

export async function resetRegistrationReminders(uid) {
  await updateDoc(doc(db, 'users', uid), {
    registrationReminderCount: 0,
    registrationReminderLastAt: null,
    registrationReminderExpiredAt: null,
    registrationReminderNextEligibleAt: null,
    updatedAt: serverTimestamp(),
  });
}

export async function expireRegistration(uid) {
  await setDoc(doc(db, 'users', uid), {
    registrationIncomplete: true,
    registrationStatus: 'expired',
    registrationExpiredAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export async function reactivateRegistration(uid) {
  await setDoc(doc(db, 'users', uid), {
    registrationIncomplete: true,
    registrationStatus: 'incomplete',
    registrationExpiredAt: null,
    registrationReminderExpiredAt: null,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export async function extendRegistration(uid, days = 7) {
  const safeDays = Math.max(1, Math.min(365, Number(days) || 7));
  await setDoc(doc(db, 'users', uid), {
    registrationExpiresAt: new Date(Date.now() + safeDays * 86400000),
    registrationExpiredAt: null,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export const registrationFriendlyError = (error, fallback = 'Unable to update the registration.') => friendlyError(error, fallback);
